import { Session } from '../api/types';
import { ScanQueue, QueuedScan } from './scanQueue';
import { ApiRequestError, SessionService, isRetryableError } from './sessionService';

export type ScanOutcome =
  /** The server has the scan; `session` is its latest state */
  | { kind: 'synced'; scan: QueuedScan; session: Session }
  /** The server refused the scan for good (expired, finished, not on crawl) */
  | { kind: 'rejected'; scan: QueuedScan; error: unknown };

export interface FlushResult {
  outcomes: ScanOutcome[];
  /** A retryable failure (offline, 429, 5xx) left scans queued */
  blocked: boolean;
}

/**
 * After a definitive error, check whether the scan landed anyway: a request
 * whose response was lost on a bad connection still counts on the server, and
 * its retry then fails with 409/410. Returns the session if the pub is
 * complete, null if it isn't, or 'unknown' if the check itself couldn't run.
 */
const sessionIfScanLanded = async (scan: QueuedScan): Promise<Session | null | 'unknown'> => {
  try {
    const session = await SessionService.getSession(scan.sessionId);
    const pub = session.pubs.find(p => p.id === scan.pubId);
    return pub?.isComplete ? session : null;
  } catch (error) {
    return isRetryableError(error) ? 'unknown' : null;
  }
};

/** Send queued scans oldest first. Stops at the first retryable failure so
 *  scans always reach the server in the order they were made. */
const flushOnce = async (): Promise<FlushResult> => {
  const outcomes: ScanOutcome[] = [];
  for (const scan of ScanQueue.all()) {
    try {
      const session = await SessionService.recordDrink(scan.pubId, scan.sessionId, scan.scannedAt);
      ScanQueue.remove(scan);
      outcomes.push({ kind: 'synced', scan, session });
    } catch (error) {
      if (isRetryableError(error)) {
        return { outcomes, blocked: true };
      }
      const landed = await sessionIfScanLanded(scan);
      if (landed === 'unknown') {
        return { outcomes, blocked: true };
      }
      ScanQueue.remove(scan);
      outcomes.push(landed
        ? { kind: 'synced', scan, session: landed }
        : { kind: 'rejected', scan, error });
    }
  }
  return { outcomes, blocked: false };
};

let running: Promise<FlushResult> | null = null;
let rerun = false;

/**
 * Sync the queue with the server. Only one flush runs at a time, so a scan is
 * never sent twice in parallel; a call made mid-flush shares that flush, which
 * then goes round again to pick up anything queued meanwhile.
 */
export const flushScanQueue = (): Promise<FlushResult> => {
  if (running) {
    rerun = true;
    return running;
  }
  running = (async () => {
    const outcomes: ScanOutcome[] = [];
    try {
      do {
        rerun = false;
        const result = await flushOnce();
        outcomes.push(...result.outcomes);
        if (result.blocked) {
          return { outcomes, blocked: true };
        }
      } while (rerun);
      return { outcomes, blocked: false };
    } finally {
      running = null;
    }
  })();
  return running;
};

/** Plain-language reason a scan was refused, for the error banner. */
export const rejectionMessage = (error: unknown, pubName?: string): string => {
  const scan = pubName ? `Your scan at ${pubName}` : 'Your scan';
  if (error instanceof ApiRequestError) {
    switch (error.code) {
      case 'SESSION_EXPIRED':
        return `${scan} couldn't be recorded: your crawl expired before it synced.`;
      case 'SESSION_FINISHED':
        return `${scan} couldn't be recorded: your crawl had already finished.`;
      case 'PUB_NOT_ON_CRAWL':
        return `${scan} couldn't be recorded: that pub isn't on your crawl.`;
      case 'SESSION_NOT_FOUND':
        return `${scan} couldn't be recorded: your crawl couldn't be found.`;
    }
  }
  return `${scan} couldn't be recorded.`;
};
